import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, BadgeCheck, Wrench, CreditCard, ShieldCheck, Award, Store, Star, Users, Package, MapPin, Sparkles, TrendingUp } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ProductCard } from '@/components/ProductCard';
import { VehicleFinder } from '@/components/VehicleFinder';
import { SponsoredCarousel } from '@/components/SponsoredCarousel';
import { PromoOffersSection } from '@/components/PromoOffersSection';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { useSEO } from '@/lib/useSEO';
import { LANDING } from '@/constants/testIds';
import { toast } from 'sonner';

const HERO_IMG = 'https://images.unsplash.com/photo-1552656967-7a0991a13906?w=1600';
const CATEGORY_FALLBACK = '/categorias/accesorios-800.webp';

const CATEGORIES = [
  { name: 'Motor', slug: 'motor' },
  { name: 'Frenos', slug: 'frenos' },
  { name: 'Suspensión', slug: 'suspension' },
  { name: 'Dirección', slug: 'direccion' },
  { name: 'Llantas y Rines', slug: 'llantas-rines' },
  { name: 'Baterías', slug: 'baterias' },
  { name: 'Aceites y Lubricantes', slug: 'aceites-lubricantes' },
  { name: 'Iluminación', slug: 'iluminacion' },
  { name: 'Sistema Eléctrico', slug: 'sistema-electrico' },
  { name: 'Carrocería', slug: 'carroceria' },
  { name: 'Aire Acondicionado', slug: 'aire-acondicionado' },
  { name: 'Accesorios', slug: 'accesorios' },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const { add } = useCart();

  useSEO({
    title: 'AutoMarket Colombia | Repuestos para carros',
    description: 'Marketplace especializado en repuestos originales y genéricos para vehículos en Colombia. Vendedores verificados, pagos seguros y envíos nacionales.',
    canonical: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'AutoMarket Colombia',
      url: window.location.origin,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${window.location.origin}/catalogo?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  });

  useEffect(() => {
    Promise.all([
      api.get('/products?limit=8').then(({ data }) => data.items || []),
      api.get('/platform/stats').then(({ data }) => data).catch(() => null),
      api.get('/sellers/top?limit=6').then(({ data }) => data.items || []).catch(() => []),
      api.get('/products?sort=newest&limit=8').then(({ data }) => data.items || []).catch(() => []),
    ]).then(([f, s, t, r]) => {
      setFeatured(f); setStats(s); setTopSellers(t); setRecentProducts(r);
    });
  }, []);

  const onAdd = (p) => { add(p); toast.success(`${p.name} agregado al carrito`); };

  return (
    <Layout>
      {/* Compact hero (reduced height) */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-900 text-white">
        <div className="absolute inset-0 opacity-25">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-14">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-orange-300 mb-3">
              <BadgeCheck className="h-3 w-3" /> Marketplace #1 de repuestos en Colombia
            </div>
            <h1 className="font-display text-2xl font-black leading-[1.1] tracking-tighter sm:text-4xl lg:text-5xl">
              El repuesto que buscas,{' '}
              <span className="text-orange-400">al mejor precio</span> y con garantía.
            </h1>
            <p className="mt-3 max-w-2xl text-xs sm:text-base leading-relaxed text-slate-300">
              Miles de repuestos originales y genéricos. Vendedores verificados, pagos 100% seguros,
              protección al comprador y envíos a toda Colombia.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 sm:gap-3">
              <Link to="/catalogo" className="flex-1 sm:flex-none min-w-0">
                <Button data-testid={LANDING.heroCta} className="w-full sm:w-auto rounded-none bg-orange-500 text-slate-900 hover:bg-orange-400 hover:-translate-y-0.5 transition-all h-11 px-4 sm:px-6 text-xs sm:text-sm font-bold shadow-lg shadow-orange-500/20">
                  Explorar catálogo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/registro?rol=seller" className="flex-1 sm:flex-none min-w-0">
                <Button data-testid={LANDING.heroSell} variant="outline" className="w-full sm:w-auto rounded-none border-white/30 bg-transparent text-white hover:bg-white hover:text-slate-900 hover:-translate-y-0.5 transition-all h-11 px-4 sm:px-6 text-xs sm:text-sm font-bold">
                  <Store className="mr-2 h-4 w-4" /> Vender en AutoMarket
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsored carousel (auto-rotates) */}
      <SponsoredCarousel />

      {/* Vehicle Finder strip (between hero and trust) */}
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <VehicleFinder />
        </div>
      </section>

      {/* Trust strip — 5 pillars */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <TrustItem icon={BadgeCheck} title="Vendedores Verificados" sub="Registro y validación de negocio" />
          <TrustItem icon={CreditCard} title="Pagos Seguros" sub="Todos los métodos de pago" />
          <TrustItem icon={ShieldCheck} title="Protección al Comprador" sub="Garantía sobre cada compra" />
          <TrustItem icon={Award} title="Garantías" sub="Definidas por el vendedor" />
          <TrustItem icon={Truck} title="Envíos Nacionales" sub="Cobertura en toda Colombia" />
        </div>
      </section>

      <PromoOffersSection />

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Categorías</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1 text-slate-900">Encuentra por tipo de repuesto</h2>
          </div>
          <Link to="/catalogo" className="hidden md:inline text-sm font-semibold text-blue-700 hover:underline">
            Ver todo →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {CATEGORIES.map((c) => (
            <Link
              key={c.name}
              data-testid={LANDING.categoryCard}
              to={`/catalogo?category=${encodeURIComponent(c.name)}`}
              className="group block border border-slate-200 bg-white hover:border-slate-900 transition-colors"
            >
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img
                  src={`/categorias/${c.slug}-800.webp`}
                  srcSet={`/categorias/${c.slug}-400.webp 400w, /categorias/${c.slug}-800.webp 800w`}
                  sizes="(min-width: 1024px) 200px, (min-width: 768px) 25vw, 50vw"
                  alt={c.name}
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                  onError={(e) => {
                    if (e.currentTarget.src !== window.location.origin + CATEGORY_FALLBACK) {
                      e.currentTarget.src = CATEGORY_FALLBACK;
                    }
                  }}
                />
              </div>
              <div className="p-2 text-center">
                <div className="font-display font-bold text-xs text-slate-900">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Destacados</div>
              <h2 className="font-display text-2xl font-black tracking-tight mt-1 text-slate-900">Lo más vendido</h2>
            </div>
            <Link to="/catalogo" className="text-sm font-semibold text-blue-700 hover:underline">
              Ver catálogo →
            </Link>
          </div>
          <div data-testid={LANDING.featuredGrid} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.product_id} product={p} onAdd={onAdd} />)}
            {featured.length === 0 && (
              <div className="col-span-full py-12 text-center text-slate-500 text-sm">Cargando productos...</div>
            )}
          </div>
        </div>
      </section>

      {/* Platform stats — social proof */}
      {stats && (
        <section data-testid="platform-stats" className="bg-slate-900 text-white border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
            <div className="text-center mb-8">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-orange-400">AutoMarket en cifras</div>
              <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">La comunidad automotriz de Colombia</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              <StatBlock icon={Store} value={stats.sellers} label="Vendedores verificados" />
              <StatBlock icon={Package} value={stats.products} label="Productos publicados" />
              <StatBlock icon={Users} value={stats.buyers} label="Compradores registrados" />
              <StatBlock icon={MapPin} value={stats.cities || 32} label="Ciudades con cobertura" />
            </div>
          </div>
        </section>
      )}

      {/* Featured stores */}
      {topSellers.length > 0 && (
        <section className="bg-white border-b border-slate-200">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Tiendas destacadas</div>
                <h2 className="font-display text-2xl font-black tracking-tight mt-1 text-slate-900">Vendedores mejor calificados</h2>
              </div>
              <Link to="/catalogo" className="hidden md:inline text-sm font-semibold text-blue-700 hover:underline">
                Ver todos →
              </Link>
            </div>
            <div data-testid="top-sellers" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {topSellers.map((s) => (
                <Link
                  key={s.user_id}
                  to={`/tienda/${s.user_id}`}
                  data-testid={`top-seller-${s.user_id}`}
                  className="group block border border-slate-200 bg-white rounded-md p-4 hover:shadow-lg hover:-translate-y-1 hover:border-orange-400 transition-all"
                >
                  <div className={`mx-auto flex h-14 w-14 items-center justify-center text-lg font-black rounded-full ${
                    s.plan === 'pro' ? 'bg-orange-500 text-slate-900' : 'bg-slate-900 text-white'
                  }`}>
                    {s.business_name?.[0]?.toUpperCase() || 'V'}
                  </div>
                  <div className="mt-2 text-center">
                    <div className="font-display font-bold text-sm text-slate-900 truncate group-hover:text-blue-700">
                      {s.business_name}
                    </div>
                    {s.city && <div className="text-[10px] text-slate-500 truncate">{s.city}</div>}
                    <div className="mt-1 flex items-center justify-center gap-1 text-[11px]">
                      {s.rating != null ? (
                        <>
                          <Star className="h-3 w-3 fill-orange-500 text-orange-500" />
                          <span className="font-semibold text-slate-900">{s.rating}</span>
                          <span className="text-slate-400">({s.reviews_count})</span>
                        </>
                      ) : (
                        <span className="text-slate-400">Sin calificaciones</span>
                      )}
                    </div>
                    <div className="mt-1 text-[10px] text-slate-500">{s.products_total} productos</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recently added */}
      {recentProducts.length > 0 && (
        <section className="bg-slate-50 border-b border-slate-200">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1.5">
                  <TrendingUp className="h-3 w-3" /> Recién publicados
                </div>
                <h2 className="font-display text-2xl font-black tracking-tight mt-1 text-slate-900">Nuevos ingresos esta semana</h2>
              </div>
              <Link to="/catalogo?sort=newest" className="text-sm font-semibold text-blue-700 hover:underline">
                Ver todos →
              </Link>
            </div>
            <div data-testid="recent-products" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {recentProducts.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} onAdd={onAdd} />)}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}

const TrustItem = ({ icon: Icon, title, sub }) => (
  <div className="flex items-center gap-2 sm:gap-3">
    <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center bg-slate-900 text-white shrink-0">
      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
    </div>
    <div className="min-w-0">
      <div className="font-semibold text-slate-900 text-xs sm:text-sm leading-tight">{title}</div>
      <div className="text-[10px] sm:text-xs text-slate-500 leading-tight">{sub}</div>
    </div>
  </div>
);

const formatStat = (n) => {
  if (n == null) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M+`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k+`;
  return `${n}+`;
};

const StatBlock = ({ icon: Icon, value, label }) => (
  <div className="text-center border border-white/10 bg-white/5 p-4 sm:p-6 rounded-md hover:bg-white/10 hover:-translate-y-1 transition-all">
    <Icon className="mx-auto h-6 w-6 sm:h-7 sm:w-7 text-orange-400" />
    <div className="mt-2 font-display text-2xl sm:text-4xl font-black tracking-tighter">{formatStat(value)}</div>
    <div className="mt-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-slate-300">{label}</div>
  </div>
);
